import {
  Button,
  Checkbox,
  Inline,
  List,
  Modal,
  type ModalProps,
  Notification,
  notifySuccess,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import {
  invoicesPath,
  useCancelContractorPaymentMutation,
} from '@omnipresentgroup/domains';
import type { ContractorPayment } from '@omnipresentgroup/types';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

type TransactionCancelationModalProps = {
  payment: ContractorPayment;
  modalProps: Omit<ModalProps, 'children'>;
  handleConfirm: () => void;
  handleCancel: () => void;
};

export const MODAL_TITLE = 'Cancel this transaction?';
export const CHECKBOX_LABEL =
  'I am sure that no money has been sent for this transaction.';

export const TransactionCancelationModal = (
  props: TransactionCancelationModalProps,
) => {
  const { modalProps, handleCancel, handleConfirm, payment } = props;

  const queryClient = useQueryClient();

  const [cancelationConfirmed, setCancelationConfirmed] = useState(false);

  const {
    mutate: cancelPayment,
    isSuccess: isCancelSuccess,
    isPending: isCancellingPayment,
  } = useCancelContractorPaymentMutation({ id: payment.id });

  const invalidateInvoicesQueries = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [invoicesPath],
    });
  }, [queryClient]);

  useEffect(() => {
    if (isCancelSuccess) {
      handleConfirm?.();
      invalidateInvoicesQueries();
      notifySuccess({ title: 'Transaction cancelled successfully' });
    }
  }, [handleConfirm, invalidateInvoicesQueries, isCancelSuccess]);

  return (
    <Modal {...modalProps} span={4}>
      <Modal.Header title={MODAL_TITLE} />

      <Modal.Body>
        <Stack gap="24">
          <Notification
            id="invoices-notification"
            title={`This transaction includes payment for the following ${
              payment.quote.invoiceBreakdown.length > 1 ? 'invoices' : 'invoice'
            }:`}
            description={
              <List
                showDisc
                gap="0"
                items={
                  payment.quote.invoiceBreakdown.map((invoice) => ({
                    id: invoice.invoiceId,
                    name: invoice.invoiceRef,
                  })) || []
                }
                keyExtractor="id"
                render={(item) => (
                  <Typography as="span" size="14">
                    {item.name}
                  </Typography>
                )}
              />
            }
          />
          <Checkbox
            id="cancelation-confirmation"
            label={CHECKBOX_LABEL}
            checked={cancelationConfirmed}
            onCheckedChange={() =>
              setCancelationConfirmed(
                (previousConfirmation) => !previousConfirmation,
              )
            }
          />
        </Stack>
      </Modal.Body>

      <Modal.Footer>
        <Inline justify="space-between" w="stretch">
          <Button variant="secondary" onClick={handleCancel}>
            No
          </Button>
          <Button
            variant="primary"
            onClick={() => cancelPayment()}
            loading={isCancellingPayment}
            disabled={!cancelationConfirmed}
          >
            Yes
          </Button>
        </Inline>
      </Modal.Footer>
    </Modal>
  );
};
